import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useTenantAuth } from "@/context/TenantAuthContext";
import { useI18n } from "@/context/I18nContext";
import { Loader2, ArrowRight, ArrowLeft, KeyRound } from "lucide-react";
import { LanguageSelect } from "@/components/LanguageSelect";

export default function TenantLogin() {
  const { tenant, login } = useTenantAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (tenant) navigate("/tenant", { replace: true }); }, [tenant, navigate]);
  if (tenant) return <Navigate to="/tenant" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(code, pin);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center px-5 relative" data-testid="tenant-login-page">
      <div className="absolute top-5 right-5"><LanguageSelect variant="dark" /></div>
      <Link to="/" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors" data-testid="tenant-login-back-home">
        <ArrowLeft className="w-4 h-4" />{t("フロントページに戻る")}
      </Link>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-[77px] w-auto object-contain bg-white rounded-lg p-2 shadow-sm" />
          <div>
            <div className="font-display font-semibold text-lg tracking-wide">NEO REAL ESTATE</div>
            <div className="text-xs text-gray-500 tracking-wider">{t("借主ポータル")}</div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-light mb-2">{t("借主ログイン")}</h1>
        <p className="text-sm text-gray-400 mb-8">{t("管理会社から発行されたコードと暗証番号でログインしてください。")}</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">{t("ログインコード")}</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="T-0001"
              autoCapitalize="characters"
              className="w-full bg-[#1b1b1b] border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-brand tracking-widest"
              data-testid="tenant-login-code"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">{t("暗証番号")}（6桁）</label>
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              type="password"
              placeholder="••••••"
              className="w-full bg-[#1b1b1b] border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-brand tracking-[0.5em]"
              data-testid="tenant-login-pin"
            />
          </div>
          {error && <div className="text-sm text-red-400" data-testid="tenant-login-error">{error}</div>}
          <button
            type="submit"
            disabled={loading || !code || pin.length < 6}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-medium px-6 py-3.5 rounded-md transition-colors"
            data-testid="tenant-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {t("ログイン")}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}
